"use client";

import { useState } from "react";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Alert } from "@/components/ui/alert/Alert";
import { Button } from "@/components/ui/button/Button";
import TextArea from "@/components/ui/form-input/TextArea";
import * as AgenticV2API from "@/services/api/agentic-v2";
import { extractApiErrorMessage } from "@/lib/extractApiErrorMessage";

export type LeadEmailDialogProps = {
  open: boolean;
  onClose: () => void;
  faClientId: string;
  leadName: string;
  toEmail: string;
};

export function buildLeadMailto(to: string, subject: string, message: string): string {
  const params = new URLSearchParams();
  if (subject.trim()) params.set("subject", subject.trim());
  if (message.trim()) params.set("body", message.trim());
  const query = params.toString();
  return `mailto:${to}${query ? `?${query}` : ""}`;
}

/** Email-a-lead dialog — sends via Mailgun, falls back to the mail app. */
export function LeadEmailDialog({ open, onClose, faClientId, leadName, toEmail }: LeadEmailDialogProps) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AgenticV2API.LeadEmailResult | null>(null);

  const sent = result?.status === "sent";
  const showFallback = Boolean(error) || (result !== null && !sent);
  const canSend = subject.trim() !== "" && message.trim() !== "" && !sending && !sent;

  const handleClose = () => {
    setSubject("");
    setMessage("");
    setError(null);
    setResult(null);
    onClose();
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const res = await AgenticV2API.sendLeadEmail(faClientId, {
        subject: subject.trim(),
        message: message.trim(),
      });
      setResult(res);
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, "Couldn't send the email"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} size="lg">
      <DialogTitle>Email {leadName}</DialogTitle>
      <DialogBody>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          To {toEmail} — sent from the team mailbox.
        </p>
        {sent ? (
          <Alert variant="info" borderless className="mt-3" data-testid="lead-email-sent">
            Email sent to {result?.to}.
          </Alert>
        ) : (
          <div className="mt-3 space-y-3">
            <div className="flex flex-col w-full">
              <label
                htmlFor="lead-email-subject"
                className="text-sm uppercase tracking-widest text-slate-700 dark:text-slate-300 font-mono"
              >
                Subject
              </label>
              <input
                id="lead-email-subject"
                data-testid="lead-email-subject"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder="What is this about?"
                className="mt-2 block w-full rounded-md bg-white px-3 py-2 text-base text-slate-900 dark:bg-slate-900/80 dark:text-slate-200 border border-slate-200 dark:border-slate-600 outline-none placeholder:text-slate-400 dark:placeholder:text-slate-500"
              />
            </div>
            <TextArea
              id="lead-email-message"
              data-testid="lead-email-message"
              label="Message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder={`Hi ${leadName.split(" ")[0] ?? "there"},`}
              rows={6}
            />
          </div>
        )}
        {error ? (
          <Alert variant="error" borderless className="mt-3" data-testid="lead-email-error">
            {error}
          </Alert>
        ) : null}
        {showFallback ? (
          <Alert variant="warning" borderless className="mt-3" data-testid="lead-email-fallback">
            Email sending isn&apos;t available — open this in your mail app instead.
          </Alert>
        ) : null}
      </DialogBody>
      <DialogActions>
        {showFallback ? (
          <a
            href={buildLeadMailto(toEmail, subject, message)}
            data-testid="lead-email-mailto"
            className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
          >
            Open in mail app
          </a>
        ) : null}
        {!sent ? (
          <Button
            type="button"
            color="indigo"
            loading={sending}
            disabled={!canSend}
            onClick={handleSend}
            data-testid="lead-email-send"
          >
            Send email
          </Button>
        ) : null}
        <Button type="button" plain onClick={handleClose} data-testid="lead-email-close">
          {sent ? "Done" : "Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
